import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useBrand } from "@/lib/brand";
import { api } from "@/lib/api";

/**
 * PageviewTracker: one aggregate-counter beacon per public route change.
 *
 * No cookie, no localStorage, no visitor id. See
 * crm/public_routes.py::track_pageview for why that makes this exempt from
 * cookie-consent requirements, and note that it is a different thing
 * entirely from the Meta pixel, which sets cookies and therefore waits for
 * consent. This one counts "what is getting looked at" for the agent.
 *
 * MOUNTED AT THE ROOT OF THE APP, NOT FROM A FOOTER. It used to be
 * rendered by SiteFooter, with a comment saying "so every tenant page gets
 * it for free". Then the Estate design shipped with its own footer and the
 * beacon silently stopped: `page_analytics` recorded nothing after 30
 * September 2026, nine days of nginx logs carried not one request to the
 * endpoint, and a live page load fired nothing. Every agency's "website
 * views" had quietly read zero for a fortnight, which is a number the
 * product shows a paying customer.
 *
 * A counter that depends on somebody remembering to render a particular
 * footer is a counter that will break again the next time a design lands.
 * So it sits at the top of the tree and decides for itself what is public.
 */

/**
 * The parts of the app that are NOT somebody visiting an agency's website.
 *
 * The CRM, the phone's web view, the Listings Manager and the three client
 * portals are all signed-in surfaces. Counting a negotiator clicking around
 * their own CRM as a website view would inflate the one number an agent
 * uses to judge their own marketing, which is worse than not counting at
 * all: a wrong number gets believed.
 */
export const PRIVATE = /^\/(crm|admin|manage|vendor|buyer|tenant|legal\/preview)(\/|$)/;

/**
 * What this page has already counted, at MODULE scope rather than in a ref.
 *
 * A ref belongs to one mounted instance, and this component is mounted from
 * two layouts and remounted by React.StrictMode, which double-invokes
 * effects in development. Measured: one page load, two beacons, `views: 2`
 * in the database for a single visit.
 *
 * StrictMode does not do that in a production build, so this would have
 * been right in the wild and wrong on every developer's screen, which is a
 * difference nobody can see and therefore the worst kind. A set that
 * outlives the component settles it in both: a path counts once per page
 * session, whoever mounts the tracker and however often.
 */
const counted = new Set();

export default function PageviewTracker() {
  const location = useLocation();
  const brand = useBrand();

  useEffect(() => {
    const tenantSlug = brand.tenant_slug;
    // "foundation" is the fallback slug an unresolved domain returns, so it
    // means "we do not know whose site this is" as often as it means our
    // own marketing site. Our own traffic is measured by the Meta pixel and
    // by the access log; an agency's is measured here.
    if (!tenantSlug || tenantSlug === "foundation") return;
    const path = location.pathname;
    if (PRIVATE.test(path)) return;
    const key = `${tenantSlug}|${path}`;
    if (counted.has(key)) return;
    counted.add(key);
    api.post("/crm/public/analytics/pageview", {
      tenant_slug: tenantSlug,
      path,
      referrer: document.referrer || "",
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, brand.tenant_slug]);

  return null;
}
